import { useMemo, useRef, type ReactNode } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { HighlightList } from '@/components/HighlightList';
import { ProfileSnippet } from '@/components/ProfileSnippet';
import { SelectionHighlighter } from '@/components/SelectionHighlighter';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAddressableEvent } from '@/hooks/useAddressableEvent';
import { useHighlightsOfSource } from '@/hooks/useHighlights';
import { markify, normalizePhrases } from '@/lib/markify';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface ArticlePageProps {
  kind: number;
  pubkey: string;
  identifier: string;
}

function articleTag(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Fallback for naddr addresses that aren't long-form articles. */
function GenericAddressableView({ event, highlightsNode }: { event: NostrEvent; highlightsNode: ReactNode }) {
  return (
    <div className="container max-w-2xl py-8 space-y-8">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="secondary">kind {event.kind}</Badge>
            <ProfileSnippet pubkey={event.pubkey} />
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{event.content}</p>
        </CardContent>
      </Card>
      {highlightsNode}
    </div>
  );
}

export function ArticlePage({ kind, pubkey, identifier }: ArticlePageProps) {
  const coordinate = `${kind}:${pubkey}:${identifier}`;
  const article = useAddressableEvent(kind, pubkey, identifier);
  const { highlights, isPending: highlightsPending } = useHighlightsOfSource({ a: coordinate });
  const queryClient = useQueryClient();
  const articleRef = useRef<HTMLElement>(null);

  const event = article.data ?? undefined;

  // Optimistically add a freshly published highlight to the rail + inline marks.
  const handlePublished = (published: NostrEvent) => {
    queryClient.setQueryData<NostrEvent[]>(['nostr', 'highlights-of', coordinate, '', ''], (old) => [
      published,
      ...(old ?? []),
    ]);
  };

  // Highlight phrases painted over the article body.
  const phrases = useMemo(
    () => normalizePhrases(highlights.map((h) => h.quote)),
    [highlights],
  );

  const markdownComponents = useMemo<Components>(
    () => ({
      p: ({ node, children, ...props }) => <p {...props}>{markify(children, phrases)}</p>,
      li: ({ node, children, ...props }) => <li {...props}>{markify(children, phrases)}</li>,
      h1: ({ node, children, ...props }) => <h1 {...props}>{markify(children, phrases)}</h1>,
      h2: ({ node, children, ...props }) => <h2 {...props}>{markify(children, phrases)}</h2>,
      h3: ({ node, children, ...props }) => <h3 {...props}>{markify(children, phrases)}</h3>,
      h4: ({ node, children, ...props }) => <h4 {...props}>{markify(children, phrases)}</h4>,
      blockquote: ({ node, children, ...props }) => <blockquote {...props}>{markify(children, phrases)}</blockquote>,
      td: ({ node, children, ...props }) => <td {...props}>{markify(children, phrases)}</td>,
      th: ({ node, children, ...props }) => <th {...props}>{markify(children, phrases)}</th>,
    }),
    [phrases],
  );

  const title = event ? articleTag(event, 'title')?.trim() || 'Untitled article' : undefined;
  const summary = event ? articleTag(event, 'summary')?.trim() : undefined;

  useSeoMeta({
    title: title ? `${title} — glowmark` : 'Article — glowmark',
    description: summary || 'Read this article and its highlights on glowmark.',
  });

  const highlightsSection = (
    <section id="highlights" className="space-y-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <Highlighter className="size-4.5 text-primary" />
        Highlights
        {highlights.length > 0 && (
          <Badge variant="secondary" className="ml-1">{highlights.length}</Badge>
        )}
      </h2>
      {highlightsPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      ) : highlights.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 px-6 text-center">
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              No one has highlighted this yet. When readers mark passages, they'll appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <HighlightList highlights={highlights} />
      )}
    </section>
  );

  if (article.isPending) {
    return (
      <AppShell>
        <div className="container max-w-2xl py-10 space-y-6">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="aspect-[2/1] w-full rounded-xl" />
          <div className="space-y-3 pt-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/5" />
          </div>
        </div>
      </AppShell>
    );
  }

  if (!event) {
    return (
      <AppShell>
        <div className="container max-w-2xl py-16">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                This article couldn't be found on your relays. It may have been deleted, or your
                relay connections may need a moment.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  if (kind !== 30023) {
    return (
      <AppShell>
        <GenericAddressableView event={event} highlightsNode={highlightsSection} />
      </AppShell>
    );
  }

  const image = sanitizeUrl(articleTag(event, 'image'));
  const publishedAt = Number(articleTag(event, 'published_at')) || event.created_at;
  const topics = event.tags.filter(([n]) => n === 't').map(([, v]) => v).slice(0, 6);

  return (
    <AppShell>
      <div className="container max-w-5xl py-8 sm:py-10">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Article */}
          <article ref={articleRef} className="min-w-0">
            <header className="space-y-4">
              <h1 className="font-serif text-3xl sm:text-4xl font-medium leading-tight tracking-tight">
                {title}
              </h1>
              {summary && (
                <p className="text-lg text-muted-foreground leading-relaxed">{summary}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                <ProfileSnippet pubkey={event.pubkey} />
                <time dateTime={new Date(publishedAt * 1000).toISOString()}>
                  {formatDate(publishedAt)}
                </time>
                {topics.map((topic) => (
                  <Badge key={topic} variant="secondary">#{topic}</Badge>
                ))}
              </div>
              {image && (
                <img
                  src={image}
                  alt=""
                  loading="lazy"
                  className="w-full rounded-xl border border-border object-cover max-h-[420px]"
                />
              )}
            </header>

            <div className="article-body mt-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {event.content}
              </ReactMarkdown>
            </div>

            {/* Highlights below the article on small screens */}
            <div className="mt-12 lg:hidden">{highlightsSection}</div>
          </article>

          {/* Sticky highlights rail on large screens */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1 pb-4">
              {highlightsSection}
            </div>
          </aside>
        </div>

        <SelectionHighlighter
          containerRef={articleRef}
          sourceContent={event.content}
          source={{ type: 'article', coordinate, authorPubkey: pubkey, label: title ?? 'this article' }}
          onPublished={handlePublished}
        />
      </div>
    </AppShell>
  );
}
